'use client'

import { accountKitConfig } from '@/config/accountkit'
import { POLYGON_RPC_URL } from '@/constants/polymarket'
import { WalletContext } from '@/providers/WalletContext'
import { TurnkeyEthersSigner } from '@/utils/TurnkeyEthersAdapter'
import {
	AlchemyAccountProvider,
	useSigner,
	useSolanaTransaction,
	useUser,
} from '@account-kit/react'
import { useQueryClient } from '@tanstack/react-query'
import { providers } from 'ethers'
import { type ReactNode, useEffect, useState } from 'react'
import {
	http,
	type WalletClient,
	createPublicClient,
	createWalletClient,
	hashMessage,
	keccak256,
	serializeTransaction,
} from 'viem'
import { toAccount } from 'viem/accounts'
import { polygon } from 'viem/chains'

const publicClient = createPublicClient({
	chain: polygon,
	transport: http(POLYGON_RPC_URL),
})

function WalletContextProvider({ children }: { children: ReactNode }) {
	const [walletClient, setWalletClient] = useState<WalletClient | null>(null)
	const [eoaAddress, setEoaAddress] = useState<`0x${string}` | undefined>(undefined)
	const [ethersSigner, setEthersSigner] = useState<TurnkeyEthersSigner | null>(null)

	const user = useUser()
	const signer = useSigner()
	const { signer: solanaSigner, connection: solanaConnection } = useSolanaTransaction()
	const solanaAddress = solanaSigner?.address

	// Only consider authenticated if user exists AND is not just an external wallet connection
	// user.type === 'eoa' means wallet connected via AppKit/Wagmi but not authenticated through AccountKit
	const authenticated = !!user && user.type !== 'eoa'

	useEffect(() => {
		async function init() {
			if (!(authenticated && signer)) {
				setWalletClient(null)
				setEthersSigner(null)
				setEoaAddress(undefined)
				return
			}

			try {
				const address = user.address as `0x${string}`
				setEoaAddress(address)

				// Create a proper viem account that wraps AccountKit's signer
				// This ensures walletClient.account.address is set correctly
				const account = toAccount({
					address,
					async signMessage({ message }) {
						// Viem's signMessage with { raw: bytes } adds the Ethereum prefix
						// and then signs. AccountKit's signMessage also adds the prefix.
						// To match viem's behavior, we need to:
						// 1. Compute the prefixed hash using viem's hashMessage
						// 2. Sign that hash directly with signRawMessage

						if (typeof message === 'string') {
							// Regular string message - AccountKit handles this correctly
							return signer.signMessage(message) as Promise<`0x${string}`>
						}

						// Raw bytes message - compute the Ethereum prefixed hash ourselves
						// then sign it directly without AccountKit adding another prefix
						const prefixedHash = hashMessage({ raw: message.raw })

						const result = await signer.inner.signRawMessage(prefixedHash)
						return result as `0x${string}`
					},
					async signTransaction(transaction) {
						const serialized = serializeTransaction(transaction)
						const hash = keccak256(serialized)
						const signature = await signer.signMessage(hash)
						return signature as `0x${string}`
					},
					// biome-ignore lint/suspicious/noExplicitAny: viem TypedDataDefinition requires flexible typing
					async signTypedData(typedData: any) {
						return signer.signTypedData({
							domain: typedData.domain,
							types: typedData.types,
							primaryType: typedData.primaryType,
							message: typedData.message,
						}) as Promise<`0x${string}`>
					},
				})

				const client = createWalletClient({
					account,
					chain: polygon,
					transport: http(POLYGON_RPC_URL),
				})

				setWalletClient(client)

				const ethersProvider = new providers.JsonRpcProvider(POLYGON_RPC_URL)
				await ethersProvider.ready
				setEthersSigner(new TurnkeyEthersSigner(client, ethersProvider))
			} catch (err) {
				console.error('Failed to initialize wallet client:', err)
				setWalletClient(null)
				setEthersSigner(null)
				setEoaAddress(undefined)
			}
		}
		init()
	}, [authenticated, user, signer])

	return (
		<WalletContext.Provider
			value={{
				eoaAddress,
				walletClient,
				publicClient,
				ethersSigner: ethersSigner,
				solanaAddress,
				solanaSigner: solanaSigner ?? null,
				solanaConnection: solanaConnection ?? null,
				isReady: authenticated && !!walletClient && !!eoaAddress,
				isSolanaReady: authenticated && !!solanaSigner && !!solanaAddress,
				authenticated,
			}}
		>
			{children}
		</WalletContext.Provider>
	)
}

export default function WalletProvider({ children }: { children: ReactNode }) {
	const queryClient = useQueryClient()

	return (
		<AlchemyAccountProvider config={accountKitConfig} queryClient={queryClient}>
			<WalletContextProvider>{children}</WalletContextProvider>
		</AlchemyAccountProvider>
	)
}
